import {
  ApolloClient,
  ApolloLink,
  from,
  HttpLink,
  InMemoryCache,
  split,
  defaultDataIdFromObject,
  Observable
} from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import cookie from 'cookie';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import { HttpOptions } from '@apollo/client/link/http';
import { FETCH_POLICY } from 'common/apollo/FETCH_POLICY';
import { safeJSON } from '@utmost.co/utmost-design-system/src/utils/safeJSON';
import { INITIAL_INFO_NAME } from 'src/providers/User/AuthUserModels';
import { getSessionConfigForHTTPHeaders } from 'utils/authSessionForCode/sessionConfig';
import { bpeConfig } from '@enterprise/common';
import { redirectToPublicPage } from 'utils/gql/redirectToPublicPage';
import { refreshAccessToken } from 'utils/gql/refreshAccessToken';
import { FetchResult } from '@apollo/client/link/core';
import { isSessionForCodeActive } from 'utils/authSessionForCode/isSessionForCodeActive';

const linkOptions: HttpOptions = {
  uri: '/graphql',
  fetch: (uri, options) => {
    let headers: Record<string, string> = {};
    const apiClient = fetch;
    if (options?.headers) {
      headers = {
        ...(options?.headers as Record<string, string>),
        'X-UTMOST-EXTERNAL-FORM': document?.location?.pathname?.indexOf?.('/external') !== -1 ? 'true' : 'false',
        'X-UTMOST-ORIGIN-SERVICE': 'OC_UI' // needed for audit log entries
      };
      const xsrfToken = cookie?.parse?.(document?.cookie)?.['XSRF-TOKEN'];
      if (xsrfToken) {
        headers['X-XSRF-TOKEN'] = xsrfToken;
      }
    }
    return apiClient(uri, {
      ...options,
      headers: {
        ...getSessionConfigForHTTPHeaders(),
        ...headers
      }
    });
  }
};

const httpLink = new HttpLink(linkOptions);
const batchHttpLink = new BatchHttpLink({
  ...linkOptions,
  batchMax: 5, // No more than 5 operations per batch
  batchInterval: 30 // Wait no more than 20ms after first batched operation
});

const errorLink = onError(({ graphQLErrors, operation, networkError, response, forward }) => {
  if (networkError && 'statusCode' in networkError && networkError.statusCode === 401 && !isSessionForCodeActive()) {
    const observable = new Observable<FetchResult<Record<string, any>>>((observer) => {
      (async () => {
        try {
          await refreshAccessToken();
          // Retry the failed request
          const subscriber = {
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer)
          };

          forward(operation).subscribe(subscriber);
        } catch (err) {
          redirectToPublicPage(undefined, window.location.href);
        }
      })();
    });
    return observable;
  }

  // Remove Exception while fetching data from error message.
  response?.errors?.forEach((error) => {
    if (error.message) {
      error.message = error.message.replace(/Exception while fetching data \(\/.*?\) : /, '');
    }
  });

  return graphQLErrors?.forEach?.((error) => {
    if (operation.operationName !== INITIAL_INFO_NAME && error.message === 'Unauthorized') {
      redirectToPublicPage();
    }
  });
});

export const omitTypename = (key: string, value: any) => (key === '__typename' ? undefined : value);

// https://github.com/apollographql/apollo-client/issues/1564#issuecomment-357492659
const typenameLink = new ApolloLink((operation, forward) => {
  if (operation.variables) {
    // eslint-disable-next-line no-param-reassign
    operation.variables = safeJSON.fromString(JSON.stringify(operation.variables), omitTypename) ?? {};
  }

  return forward(operation);
});

export const apolloClient = new ApolloClient({
  link: split(
    (operation) =>
      operation.getContext().batch !== false &&
      process.env.NODE_ENV !== 'test' &&
      localStorage.getItem('automatic-requests-batching') !== 'false',
    from([typenameLink, errorLink, batchHttpLink]),
    from([typenameLink, errorLink, httpLink])
  ),
  cache: new InMemoryCache({
    addTypename: false,
    dataIdFromObject: (responseObject) => {
      // eslint-disable-next-line no-underscore-dangle
      const typename = responseObject.__typename;

      // if the object has a uid - then use that
      if (responseObject.uid && typename) {
        return `${typename}:${responseObject.uid}`;
      }

      return defaultDataIdFromObject(responseObject);
    }
  }),

  defaultOptions: { watchQuery: { fetchPolicy: FETCH_POLICY.NO_CACHE, errorPolicy: 'all' } },
  headers: {
    'X-UTMOST-EXTERNAL-FORM': document?.location?.pathname?.indexOf?.('/external') !== -1 ? 'true' : 'false',
    'X-UTMOST-ORIGIN-SERVICE': 'OC_UI' // needed for audit log entries
  },
  connectToDevTools: shouldApolloBeExposed()
});

function shouldApolloBeExposed() {
  return (
    !!bpeConfig.environmentType &&
    (bpeConfig.environmentType.includes('DEV') ||
      bpeConfig.environmentType.includes('IMPL') ||
      bpeConfig.environmentType.includes('POC') ||
      bpeConfig.environmentType.includes('PENTEST'))
  );
}

/**
 * Pass in query name to clear from cache.
 *
 * As we have a strong caching policy in the app, it can
 * be useful to clear specific queries after create mutations
 * to ensure that new items are visible.
 *
 * @param query Apollo query name.
 */
export const deleteQueryFromCache = (query: string): void => {
  apolloClient.cache.evict({ fieldName: query });
  apolloClient.cache.gc();
};
